/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Save, Send, Loader2, Trash2, ShieldCheck, AlertTriangle, CheckCircle2, Eye, EyeOff, RefreshCw, Server, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { getEmailConfig, saveEmailConfig, deleteEmailConfig, sendTestEmail } from '@/lib/emailConfig';
import { ErrorState } from '@/components/UIExtras';

/**
 * EmailSettingsPage — /admin/email
 * Configurazione SMTP della clinica (o SendGrid) dal pannello: credenziali cifrate AES-256-GCM nel DB,
 * precedenza sul .env, pulsante "Invia email di prova" che spedisce la vera notifica referto (GDPR-safe).
 * UX: ogni azione asincrona disabilita i pulsanti e mostra uno spinner; errori mostrati con fallback eleganti.
 */

const EMPTY = { provider: 'smtp', preset: 'custom', host: '', port: 587, starttls: true, user: '', password: '', sender_email: '', sender_name: '', enabled: true };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const ActiveBadge = ({ active }) => {
  if (!active) return null;
  const p = active.provider;
  const tone = p === 'dev' ? 'border-warning/60 text-warning' : 'border-success/50 text-success';
  const label = p === 'dev' ? 'Nessun provider: le email finiscono solo nei log' : `${p.toUpperCase()} attivo · origine ${active.source === 'database' ? 'pannello' : '.env'}`;
  return <Badge variant="outline" className={cn('text-[10px] gap-1', tone)} data-testid="email-active-badge">{p === 'dev' ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} {label}</Badge>;
};

export default function EmailSettingsPage() {
  const { user } = useAuth() || {};
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [testTo, setTestTo] = useState('');
  const busy = saving || testing || deleting;

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const c = await getEmailConfig();
      setCfg(c);
      const preset = (c.presets || []).find((p) => p.host && p.host === c.host)?.id || (c.provider === 'sendgrid' ? 'sendgrid' : 'custom');
      setForm({ provider: c.provider || 'smtp', preset, host: c.host || '', port: c.port || 587, starttls: c.starttls ?? true, user: c.user || '', password: '', sender_email: c.sender_email || '', sender_name: c.sender_name || '', enabled: c.configured ? c.enabled : true });
    } catch (e) {
      setLoadError(e?.response?.data?.detail || (e?.code === 'ERR_NETWORK' ? 'Rete non disponibile' : 'Impossibile caricare la configurazione email'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const presets = useMemo(() => cfg?.presets || [], [cfg]);
  const preset = useMemo(() => presets.find((p) => p.id === form.preset) || presets[0], [presets, form.preset]);

  const applyPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setForm((f) => ({ ...f, preset: id, provider: id === 'sendgrid' ? 'sendgrid' : 'smtp', host: p.host || (id === 'custom' ? f.host : ''), port: p.port || f.port || 587, starttls: p.starttls, user: id === 'resend' ? 'resend' : f.user }));
  };

  const validate = () => {
    if (!form.sender_email.trim()) return 'Inserisci l\'indirizzo mittente';
    if (form.provider === 'smtp' && !form.host.trim()) return 'Inserisci l\'host SMTP';
    if (!form.password && !cfg?.has_password) return 'Inserisci la password SMTP (o la API key)';
    return null;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = { provider: form.provider, host: form.host.trim(), port: Number(form.port) || 587, starttls: !!form.starttls, user: form.user.trim(), sender_email: form.sender_email.trim(), sender_name: form.sender_name.trim(), enabled: !!form.enabled };
      if (form.password) payload.password = form.password;
      const c = await saveEmailConfig(payload);
      setCfg(c); setForm((f) => ({ ...f, password: '' }));
      toast.success('Configurazione email salvata (credenziali cifrate). Ora puoi inviare l\'email di prova.');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || (e2?.code === 'ERR_NETWORK' ? 'Rete non disponibile: riprova' : 'Salvataggio non riuscito'));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await sendTestEmail(testTo.trim() || null);
      toast.success(`Email di prova inviata a ${r.to} via ${r.provider} in ${r.latency_ms} ms. Controlla la casella (anche spam).`);
      setCfg((c) => (c ? { ...c, last_test: r } : c));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || (e?.code === 'ERR_NETWORK' ? 'Rete non disponibile: impossibile contattare il server' : 'Invio di prova non riuscito'), { duration: 8000 });
      load();
    } finally { setTesting(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try { const c = await deleteEmailConfig(); setCfg(c); setForm(EMPTY); toast.success('Configurazione rimossa: il sistema usa di nuovo il .env'); } catch (e) { toast.error(e?.response?.data?.detail || 'Rimozione non riuscita'); } finally { setDeleting(false); }
  };

  if (user && !user.is_admin) {
    return <Card className="rounded-xl"><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="email-not-admin">Accesso riservato agli amministratori.</CardContent></Card>;
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="email-settings-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Mail className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display font-bold text-xl">Email della clinica (SMTP)</h1>
            <p className="text-xs text-muted-foreground">Le notifiche ai pazienti (referti, OTP, magic link) partono dal tuo server SMTP privato o da un provider low-cost. Password cifrata AES-256-GCM, mai mostrata.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ActiveBadge active={cfg?.active} />
          <Button variant="ghost" size="sm" onClick={load} disabled={loading || busy} className="rounded-xl h-9" aria-label="Ricarica" data-testid="email-reload-btn"><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /></Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]"><Skeleton className="h-[520px] rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={load} testId="email-load-error" />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
          <Card className="rounded-xl" data-testid="email-config-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Server className="h-4 w-4 text-primary" /> Credenziali di invio</CardTitle>
              <CardDescription>Scegli il provider, inserisci utente e password, salva. Il file .env resta come fallback.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label>Provider</Label>
                  <Select value={form.preset} onValueChange={applyPreset} disabled={busy}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="email-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {preset?.hint && <p className="text-[11px] text-muted-foreground mt-1" data-testid="email-preset-hint">{preset.hint}</p>}
                </div>

                {form.provider === 'smtp' && (
                  <div className="grid gap-3 sm:grid-cols-[1fr_110px_auto] items-end">
                    <div><Label>Host SMTP</Label><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="smtp.clinica.it" disabled={busy} className="h-11 rounded-xl font-mono text-xs" data-testid="email-host" /></div>
                    <div><Label>Porta</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} disabled={busy} className="h-11 rounded-xl" data-testid="email-port" /></div>
                    <div className="flex items-center gap-2 h-11"><Switch checked={form.starttls} onCheckedChange={(v) => setForm({ ...form, starttls: v })} disabled={busy} data-testid="email-starttls" /><span className="text-xs">STARTTLS</span></div>
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  {form.provider === 'smtp' && (
                    <div><Label>Utente</Label><Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder="noreply@clinica.it" autoComplete="off" disabled={busy} className="h-11 rounded-xl" data-testid="email-user" /></div>
                  )}
                  <div className={form.provider === 'smtp' ? '' : 'sm:col-span-2'}>
                    <Label>{form.provider === 'sendgrid' ? 'API key SendGrid' : 'Password SMTP'} {cfg?.has_password && <span className="text-muted-foreground font-normal">(salvata: {cfg.password_masked})</span>}</Label>
                    <div className="relative">
                      <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={cfg?.has_password ? 'Lascia vuoto per non cambiarla' : '••••••••'} autoComplete="new-password" disabled={busy} className="h-11 rounded-xl pr-11" data-testid="email-password" />
                      <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground" aria-label={showPass ? 'Nascondi' : 'Mostra'} data-testid="email-password-toggle">{showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label>Mittente (email) *</Label><Input type="email" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="no-reply@clinica.it" disabled={busy} className="h-11 rounded-xl" data-testid="email-sender" /></div>
                  <div><Label>Nome mittente</Label><Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="Clinica San Marco" disabled={busy} className="h-11 rounded-xl" data-testid="email-sender-name" /></div>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-border/70 p-3">
                  <div className="text-sm"><b>Invio attivo</b><p className="text-[11px] text-muted-foreground">Disattiva per tornare temporaneamente al .env / modalità log.</p></div>
                  <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} disabled={busy} data-testid="email-enabled" />
                </div>

                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border/70">
                  <Button type="submit" disabled={busy} className="h-11 rounded-xl" data-testid="email-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? 'Salvataggio…' : 'Salva credenziali'}
                  </Button>
                  {cfg?.configured && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" variant="ghost" disabled={busy} className="h-11 rounded-xl text-destructive hover:text-destructive" data-testid="email-delete-btn">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Rimuovi</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-xl">
                        <AlertDialogHeader><AlertDialogTitle>Rimuovere la configurazione email?</AlertDialogTitle><AlertDialogDescription>Le credenziali cifrate verranno eliminate dal database. Il sistema tornerà al file .env (o alla modalità log).</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel><AlertDialogAction onClick={remove} className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="email-delete-confirm">Rimuovi</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> AES-256-GCM · audit SHA-256</span>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="rounded-xl" data-testid="email-test-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Email di prova</CardTitle>
                <CardDescription>Invia la vera notifica «Nuovo referto disponibile» (testo GDPR-safe, senza dati clinici) al mittente o a un indirizzo a scelta.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><Label>Destinatario (opzionale)</Label><Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={cfg?.sender_email || 'default: il mittente'} disabled={busy} className="h-11 rounded-xl" data-testid="email-test-to" /></div>
                <Button onClick={test} disabled={busy || cfg?.active?.provider === 'dev'} className="h-11 rounded-xl w-full" data-testid="email-test-btn">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {testing ? 'Invio in corso…' : 'Invia email di prova'}
                </Button>
                {cfg?.active?.provider === 'dev' && <p className="text-[11px] text-warning flex items-start gap-1" data-testid="email-test-disabled-hint"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Salva prima le credenziali: senza provider attivo le email vengono solo scritte nei log del server.</p>}
                {cfg?.last_test && (
                  <div className={cn('rounded-xl border p-3 text-xs space-y-1', cfg.last_test.ok ? 'border-success/40 bg-success/5' : 'border-destructive/40 bg-destructive/5')} data-testid="email-last-test">
                    <div className="flex items-center gap-1 font-semibold">{cfg.last_test.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <AlertTriangle className="h-3.5 w-3.5 text-destructive" />} Ultimo test: {cfg.last_test.ok ? 'inviata' : 'fallita'} · {fmtDate(cfg.last_test.at)}</div>
                    <div className="text-muted-foreground">a {cfg.last_test.to} via {cfg.last_test.provider}{cfg.last_test.latency_ms != null && ` · ${cfg.last_test.latency_ms} ms`}</div>
                    {cfg.last_test.error && <div className="font-mono break-all text-destructive">{cfg.last_test.error}</div>}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-xl" data-testid="email-privacy-card">
              <CardContent className="p-4 text-xs text-muted-foreground space-y-2">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm"><ShieldCheck className="h-4 w-4 text-primary" /> Cosa riceve il paziente</div>
                <p className="rounded-xl bg-muted/40 p-3 italic">“Gentile Paziente, un nuovo referto è disponibile sul portale della sua clinica di fiducia. Acceda in totale sicurezza per visualizzarlo.”</p>
                <p>Nessun nome, titolo, medico o dato clinico: solo un link monouso valido 15 minuti. Ogni invio e ogni accesso sono tracciati nell'Audit Log SHA-256.</p>
                {cfg?.updated_at && <p data-testid="email-updated-at">Ultimo aggiornamento: {fmtDate(cfg.updated_at)}</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
